import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminVerification } from '@/hooks/useAdmin';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';

interface AdminGuardProps {
  children: React.ReactNode;
}

/**
 * AdminGuard - Protects admin routes with server-side verification
 *
 * Security measures:
 * 1. Server-side admin role verification (not just frontend check)
 * 2. Loading state while verifying
 * 3. Redirect to dashboard if not admin
 * 4. No flash of protected content
 */
export function AdminGuard({ children }: AdminGuardProps) {
  const { t } = useTranslation();
  const { data: isAdmin, isLoading, error } = useAdminVerification();

  // Show loading while verifying admin status
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" />
          <p className="text-[hsl(var(--muted-foreground))]">
            {t('admin.verifying', 'Verifying access...')}
          </p>
        </div>
      </div>
    );
  }

  // Error during verification
  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <ShieldAlert className="h-16 w-16 text-[hsl(var(--destructive))] mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {t('admin.verificationError', 'Verification Error')}
          </h3>
          <p className="text-[hsl(var(--muted-foreground))] text-center">
            {t('admin.verificationErrorDesc', 'Could not verify admin access. Please try again.')}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Not admin - redirect to dashboard
  if (isAdmin !== true) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admin verified - render children
  return <>{children}</>;
}

export default AdminGuard;
